import { Component, OnInit } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { TableModule } from 'primeng/table';
import { DatePickerModule } from 'primeng/datepicker';
import { DialogModule } from 'primeng/dialog';
import { ActivatedRoute, Router, RouterLink } from '@angular/router';
import { Battle } from '../../models/battle';
import { MomentModule } from 'ngx-moment';
import moment from 'moment';
import { BattleService } from '../../services/battle.service';
import { AdminPageHeaderComponent } from '../shared/admin-page-header/admin-page-header.component';

@Component({
  selector: 'app-battle-list',
  standalone: true,
  imports: [CommonModule, FormsModule, TableModule, DatePickerModule, MomentModule, DialogModule, AdminPageHeaderComponent, RouterLink],
  templateUrl: './battle-list.component.html',
  styleUrls: ['./battle-list.component.scss'],
})
export class BattleListComponent implements OnInit {
  searchTerm = '';
  battles: Battle[] = [];
  isLoading = false;
  isFinishedPage = false;
  displayPlayerModal = false;
  displayNewModal = false;
  selectedBattle: Battle | null = null;
  since: moment.Moment | null = null;
  until: moment.Moment | null = null;
  rangeDates: (Date | null)[] = [];
  showDatePicker = false;
  showSecondConfirmation = false;
  totalItems = 0;
  page = 1;
  rowsPerPage = 10;

  constructor(
    private router: Router,
    private route: ActivatedRoute,
    private battleService: BattleService
  ) {
  }

  ngOnInit() {
    this.isFinishedPage = this.router.url.includes('battle-list-finished');
    this.route.queryParams.subscribe(params => {
      this.searchTerm = params['search'] || '';
      this.page = parseInt(params['page'] || '1', 10);

      if (params['since'] || params['until']) {
        const def = this.getDefaultRange();
        const since = params['since']
          ? moment(params['since']).startOf('day')
          : def.since;
        const until = params['until']
          ? moment(params['until']).startOf('day')
          : def.until;
        this.applyRange(since, until);
      } else {
        if (this.isFinishedPage) {
          this.applyRange(null, null);
        } else {
          const def = this.getDefaultRange();
          this.applyRange(def.since, def.until);
        }
      }

      this.fetchBattles();
    });
  }

  private getDefaultRange(): { since: moment.Moment; until: moment.Moment } {
    const since = moment().subtract(1, 'year').startOf('day');
    const until = moment().startOf('day');
    return { since, until };
  }

  private applyRange(since: moment.Moment | null, until: moment.Moment | null): void {
    this.since = since;
    this.until = until;
    if (since && until) {
      this.rangeDates = [since.toDate(), until.toDate()];
    } else {
      this.rangeDates = [];
    }
  }

  onSearchChange() {
    this.resetPageAndUpdate();
  }

  onFinishedClick() {
    this.router.navigate(['/dashboard/battle-list-finished']);
  }

  onPendingClick() {
    this.router.navigate(['/dashboard/battle-list']);
  }

  setDateRange() {
    const [d0, d1] = this.rangeDates.map(d => d && moment(d).startOf('day'));
    
    if (!d0 && !d1) {
      this.applyRange(null, null);
    } else {
      const { since: defaultSince, until: defaultUntil } = this.getDefaultRange();
      const since = d0 && d1 ? moment.min(d0, d1) : (d0 || defaultSince);
      const until = d0 && d1 ? moment.max(d0, d1) : (d1 || defaultUntil);
      this.applyRange(since, until);
    }

    this.resetPageAndUpdate();
  }

  toggleDateFilter() {
    this.showDatePicker = !this.showDatePicker;
    if (!this.showDatePicker) {
      if (this.since && this.until) {
        this.rangeDates = [this.since.toDate(), this.until.toDate()];
      } else {
        this.rangeDates = [];
      }
      this.resetPageAndUpdate();
    }
  }

  clearFiltersAndRefresh(): void {
    this.searchTerm = '';
    this.page = 1;
    if (this.isFinishedPage) {
      this.applyRange(null, null);
    } else {
      const { since, until } = this.getDefaultRange();
      this.applyRange(since, until);
    }
    this.showDatePicker = false;

    this.router.navigate([], {
      relativeTo: this.route,
      queryParams: { page: 1, search: null, since: null, until: null },
    });
  }

  private resetPageAndUpdate(): void {
    this.page = 1;
    this.updateQueryParams();
  }

  onLazyLoad(event: any) {
    const newPage = Math.floor(event.first / this.rowsPerPage) + 1;
    if (newPage !== this.page) {
      this.page = newPage;
      this.updateQueryParams();
    }
  }

  private updateQueryParams(): void {
    this.router.navigate([], {
      relativeTo: this.route,
      queryParams: {
        page: this.page,
        search: this.searchTerm || null,
        since: this.since?.format('YYYY-MM-DD') || null,
        until: this.until?.format('YYYY-MM-DD') || null,
      },
    });
  }

  fetchBattles() {
    this.isLoading = true;
    const params: any = {
      is_finished: this.isFinishedPage,
      page: this.page,
      page_size: this.rowsPerPage,
    };

    const searchValue = this.searchTerm.trim();
    if (searchValue) {
      params.search = searchValue;
      if (/^\d+$/.test(searchValue)) params.exact_match = true;
    }

    const { queryParamMap } = this.route.snapshot;
    const since = queryParamMap.get('since');
    const until = queryParamMap.get('until');
    if (since) params.since = since;
    if (until) params.until = until;

    this.battleService.getBattles(params).subscribe({
      next: (response) => {
        this.totalItems = response.count || 0;
        this.battles = response.results || [];
        this.isLoading = false;
      },
      error: (err) => {
        this.battles = [];
        this.totalItems = 0;
        this.isLoading = false;
      }
    });
  }

  resolveBattle(battle: Battle) {
    if (battle.public_id) {
      this.battleService.resolveBattle(battle.public_id).subscribe({
        next: () => this.fetchBattles(),
        error: (err) => {
        }
      });
    }
  }

  refundBattle(publicId: string) {
    if (publicId) {
      this.battleService.refundBattle(publicId).subscribe({
        next: () => this.fetchBattles(),
        error: (err) => {
        }
      });
    }
  }

  formatDateTime(dateString: string): string {
    return dateString ? moment(dateString).format('DD [de] MMMM [de] YYYY [às] HH:mm:ss') : '-';
  }

  openPlayerModal(battle: Battle): void {
    this.selectedBattle = { ...battle };
    this.displayPlayerModal = true;
  }

  closePlayerModal(): void {
    this.displayPlayerModal = false;
    this.selectedBattle = null;
  }

  openNewModal(battle: Battle): void {
    this.selectedBattle = { ...battle };
    this.displayNewModal = true;
  }

  closeNewModal(): void {
    this.displayNewModal = false;
    this.selectedBattle = null;
  }

  cancelRefund() {
    this.closeNewModal();
  }

  confirmRefund(): void {
    if (!this.selectedBattle?.public_id) return;
    
    this.battleService.refundBattle(this.selectedBattle.public_id).subscribe({
      next: () => {
        this.fetchBattles();
        this.closeNewModal();
      },
      error: (err) => {
      }
    });
  }

  getMaxStake(battle: Battle): number {
    if (!battle?.players || battle.players.length === 0) return 0;
    
    let maxCoins = 0;
    for (const player of battle.players) {
      if (player?.coins) {
        const coins = Math.abs(player.coins);
        if (coins > maxCoins) {
          maxCoins = coins;
        }
      }
    }
    
    return maxCoins;
  }

  getResultStatus(battle: Battle): string {
    if (!battle?.is_finished) return 'Em Andamento';
    
    const players = battle.players || [];
    if (players.length < 2) return 'Finalizada';
    
    for (const player of players) {
      if (!player?.result) continue;
      const result = player.result.toUpperCase();
      if (result === 'WON' || result === 'SURRENDERED' || result === 'FORFEIT') {
        return `${result} - ${player.user_id}`;
      }
    }
    
    return 'Finalizada';
  }

  getPlayerResult(battle: Battle, playerIndex: number): string {
    if (!battle?.is_finished) return 'Em Andamento';
    
    const player = battle.players?.[playerIndex];
    if (!player?.result) return '-';
    
    return player.result;
  }

  getResultClass(battle: Battle): string {
    if (!battle?.is_finished) return '';
    
    const players = battle.players || [];
    
    for (const player of players) {
      if (!player?.result) continue;
      const result = player.result.toUpperCase();
      
      if (result === 'WON') return 'result-won';
      if (result === 'SURRENDERED' || result === 'SURRENDER') return 'result-surrendered';
      if (result === 'FORFEIT') return 'result-forfeit';
    }
    
    return '';
  }

}

